import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { gradeLevels } from '../data/words';
import { Plus, User, BookOpen, Trophy, Gift, LogOut, Trash2, Star, Heart, X, Shield, Sparkles, Lock, BadgeCheck, Receipt } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { addChild, fetchChild, fetchCompleteResult, deleteChild } from '../services/api';
import ekoIcon from '../assets/eko-_transparent-removebg-preview.png';


interface User {
  id: string;
  email: string;
  name: string;
  // role: 'parent' | 'teacher'; // Removed role field to match Authentication.tsx
  createdAt: string;
}

interface PlayerProfile {
  id: string;
  name: string;
  age: number;
  gradeLevel: number;
  gender?: 'male' | 'female' | 'rather_not_say' | 'unspecified';
  wordsCompleted: number;
  totalWordsAttempted: number;
  averageAccuracy: number;
  lastPlayed: string;
  avatar?: string; // Optional avatar property
  paymentStatus?: 'paid' | 'unpaid';
}

interface DashboardProps {
  onStartGame: (profile: PlayerProfile, gradeLevel: number, testType: 'speaking' | 'spelling') => void;
  currentUser: User;
  onLogout: () => void;
  isAdmin?: boolean;
  adminBypassPayment?: boolean;
  onSwitchToAdmin?: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ 
  onStartGame, 
  currentUser, 
  onLogout, 
  isAdmin = false,
  adminBypassPayment = false,
  onSwitchToAdmin
}) => {
  const [profiles, setProfiles] = useState<PlayerProfile[]>([]);
  const [showAddProfile, setShowAddProfile] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<PlayerProfile | null>(null);
  const [selectedGradeLevel, setSelectedGradeLevel] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<string | null>(null);
  const [showStartTestModal, setShowStartTestModal] = useState(false);
  const navigate = useNavigate();
  const [newProfile, setNewProfile] = useState({
    name: '',
    age: '',
    gradeLevel: undefined as number | undefined,
    gender: undefined as 'male' | 'female' | 'rather_not_say' | undefined
  });
  const [ageError, setAgeError] = useState<string>("");
  const [dashboardError, setDashboardError] = useState<string>("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [deletingProfile, setDeletingProfile] = useState<boolean>(false);
  const [showAssessmentGuide, setShowAssessmentGuide] = useState<boolean>(false);
  const [dontShowGuide, setDontShowGuide] = useState<boolean>(() => localStorage.getItem('skip-assessment-guide') === 'true');

 
  useEffect(() => {
    if (selectedProfile) {
      setSelectedGradeLevel(selectedProfile.gradeLevel);
    } else {
      setSelectedGradeLevel(-1); // Reset when no profile is selected
    }
  }, [selectedProfile]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

useEffect(() => {
  const loadChildren = async () => {
    setIsLoading(true);
    setDashboardError("");
    try {
      // Always fetch fresh from the backend so payment status is never stale.
      const data = await fetchChild();
      if (Array.isArray(data)) {
        const mappedProfiles: PlayerProfile[] = data.map((child) => {
          const gradeIndex = gradeLevels.findIndex(
            (g) => g.name.toLowerCase() === child.grade.toLowerCase()
          );
          return {
            id: child.child_id,
            name: child.name,
            age: child.age,
            gradeLevel: gradeIndex !== -1 ? gradeIndex : 0,
            gender: (child.gender === 'male' || child.gender === 'female' || child.gender === 'rather_not_say') ? child.gender : 'unspecified',
            wordsCompleted: 0,
            totalWordsAttempted: 0,
            averageAccuracy: 0,
            lastPlayed: new Date().toISOString(),
            paymentStatus: child.payment_status === 'paid' ? 'paid' : 'unpaid',
          };
        });
        localStorage.setItem('phonics-profiles', JSON.stringify(mappedProfiles));
        setProfiles(mappedProfiles);
      } else {
        setDashboardError("Unexpected response from server. Please try again later.");
      }
    } catch (err: any) {
      // Fall back to cached profiles (offline / transient errors)
      const savedProfiles = localStorage.getItem('phonics-profiles');
      if (savedProfiles) {
        try {
          setProfiles(JSON.parse(savedProfiles));
        } catch {
          setDashboardError(err?.message || "Failed to fetch profiles. Please check your connection and try again.");
        }
      } else {
        setDashboardError(err?.message || "Failed to fetch profiles. Please check your connection and try again.");
      }
    }
    setIsLoading(false);
  };
  loadChildren();
}, []);

  const saveProfiles = (updatedProfiles: PlayerProfile[]) => {
    localStorage.setItem('phonics-profiles', JSON.stringify(updatedProfiles));
    setProfiles(updatedProfiles);
  };

  const handleAddProfile = async () => {
    setDashboardError("");
    // Validate age again before submission
    const ageNumber = parseInt(newProfile.age || '', 10);
    if (isNaN(ageNumber) || ageNumber < 4 || ageNumber > 10) {
      setAgeError('Please enter the age from 4 to 10');
      return;
    }

    if (newProfile.name.trim() && newProfile.age && newProfile.gradeLevel !== undefined) {
      let response = null;
      try {
        response = await addChild({
          name: newProfile.name.trim(),
          age: ageNumber,
          grade: gradeLevels[newProfile.gradeLevel].name,
          gender: newProfile.gender
        });
        const profile: PlayerProfile = {
          id: response?.child_id || new Date().getTime().toString(),
          name: newProfile.name.trim(),
          age: ageNumber,
          gradeLevel: newProfile.gradeLevel,
          gender: newProfile.gender,
          wordsCompleted: 0,
          totalWordsAttempted: 0,
          averageAccuracy: 0,
          lastPlayed: new Date().toISOString(),
          paymentStatus: response?.payment_status === 'paid' ? 'paid' : 'unpaid'
        };
        const updatedProfiles = [...profiles, profile];
        saveProfiles(updatedProfiles);
        setNewProfile({ name: '', age: '', gradeLevel: undefined as number | undefined, gender: undefined as 'male' | 'female' | 'rather_not_say' | undefined });
        setShowAddProfile(false);
      } catch (error: any) {
        setDashboardError(error?.message || "Failed to add profile. Please try again.");
      }
    }
  };

  const handleDeleteProfile = async (profileId: string) => {
    setDeletingProfile(true);
    try {
      await deleteChild(profileId);

      // Update local storage and state
      const updatedProfiles = profiles.filter(p => p.id !== profileId);
      saveProfiles(updatedProfiles);
      
      // Clear selected profile if it was the deleted one
      if (selectedProfile?.id === profileId) {
        setSelectedProfile(null);
        setShowStartTestModal(false);
      }
    } catch (error: any) {
      console.error('Error deleting profile:', error);
      setDashboardError(error?.message || "Failed to delete profile. Please try again.");
    } finally {
      setDeletingProfile(false);
    }
  };

  const handleProfileClick = (profile: PlayerProfile) => {
    setSelectedProfile(profile);
    // Just select the profile, don't navigate immediately
  };

  const proceedToJourney = () => {
    if (!selectedProfile || selectedGradeLevel < 0) return;
    navigate('/app', { 
      state: { 
        showLearningJourney: true,
        profile: {
          ...selectedProfile,
          gradeLevel: selectedGradeLevel
        }
      } 
    });
  };

  // Admins with the bypass-payment toggle on can open the assessment for
  // any child, paid or not. When the toggle is off they follow the normal
  // payment flow.
  const bypassActive = !!isAdmin && !!adminBypassPayment;
  const canStartAssessment = (profile: PlayerProfile) =>
    bypassActive || profile.paymentStatus === 'paid';

  const handleStartJourney = () => {
    if (!selectedProfile || selectedGradeLevel < 0) return;
    // Payment gate: unpaid children go to the payment page instead of the assessment.
    if (!canStartAssessment(selectedProfile)) {
      navigate('/app/payment', { state: { preselectChildId: selectedProfile.id } });
      return;
    }
    if (dontShowGuide) {
      proceedToJourney();
    } else {
      setShowAssessmentGuide(true);
    }
  };

  const handleBeginAssessment = () => {
    setShowAssessmentGuide(false);
    proceedToJourney();
  };

  const formatLastPlayed = (dateString: string) => {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-yellow-500 border-t-transparent"></div>
          <p className="mt-4 text-lg text-gray-600">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        {/* Dashboard Header */}
        <div className="relative mb-6">
          <div className="absolute -top-6 -left-8 w-64 h-40 bg-gradient-to-r from-[#FFF2E6] to-[#FFF0F6] rounded-full opacity-60 blur-2xl transform rotate-12 pointer-events-none -z-10"></div>
          <header className="w-full bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white text-4xl sm:text-5xl lg:text-6xl font-bold py-6 px-6 sm:px-8 rounded-lg shadow-md mb-2 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-[#F7B26A]/20 to-[#ECA7C1]/20 rounded-lg -z-10"></div>
            <div className="flex items-center justify-between">
              <h1 className="flex items-center gap-4">
                <span className="inline-block w-12 h-12 rounded-full bg-white/30 flex items-center justify-center text-2xl">🏠</span>
                <span className="text-xl sm:text-2xl lg:text-3xl">Dashboard</span>
              </h1>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/app/payment-history')}
                  className="flex items-center gap-2 bg-white/90 text-[#1F224B] px-3 py-1.5 rounded-lg shadow-sm hover:brightness-95 transition-all font-semibold text-sm"
                  title="View payment history"
                >
                  <Receipt className="w-4 h-4" />
                  <span className="hidden md:inline">Payment History</span>
                </button>
                {isAdmin && onSwitchToAdmin && (
                  <button 
                    onClick={onSwitchToAdmin} 
                    className="flex items-center gap-2 bg-gradient-to-r from-[#A6A1F7] to-[#8E7BFF] text-white px-3 py-1.5 rounded-lg shadow-sm hover:brightness-95 transition-all font-semibold text-sm"
                  >
                    <Shield className="w-4 h-4" />
                    <span className="hidden md:inline">Admin Panel</span>
                  </button>
                )}
                <button onClick={onLogout} className="flex items-center gap-2 bg-white/90 text-[#1F224B] px-2 py-1 rounded-lg shadow-sm hover:brightness-95 text-sm">
                  <LogOut className="w-4 h-4" />
                  <span className="hidden md:inline">Logout</span>
                </button>
              </div>
            </div>
          </header>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Player Profiles */}
          <div className="lg:col-span-2">
            <Card className="p-6 bg-white/95 backdrop-blur border-4  rounded-3xl shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-[#1F224B] flex items-center gap-3">
                  <User className="w-6 h-6 text-[#FF8A7A]" />
                  Learning Profiles
                </h2>

                <Dialog open={showAddProfile} onOpenChange={setShowAddProfile}>
                  <DialogTrigger asChild>
                    <Button className="rounded-2xl btn-bouncy bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white px-4 py-2 shadow-md border-0 hover:brightness-95 flex items-center">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Child
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-3xl">
                    <DialogHeader>
                      <DialogTitle className="text-2xl text-[#1F224B] flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F7B26A] to-[#ECA7C1] flex items-center justify-center">
                          <Plus className="w-5 h-5 text-white" />
                        </div>
                        Add New Profile
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      {dashboardError && (
                        <div className="text-center text-red-600 font-semibold mb-2 bg-red-50 border border-red-200 rounded-xl p-3">
                          {dashboardError}
                        </div>
                      )}
                      <div>
                        <Label htmlFor="name" className="text-[#1F224B] font-semibold">Child's Name</Label>
                        <Input
                          id="name"
                          value={newProfile.name}
                          onChange={(e) => setNewProfile({...newProfile, name: e.target.value})}
                          placeholder="Enter child's name"
                          className="rounded-xl border-2 border-[#EDE9FF] focus:border-[#8E7BFF] h-12 mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="age" className="text-[#1F224B] font-semibold">Age</Label>
                        <Input
                          id="age"
                          type="number"
                          min="4"
                          max="10"
                          value={newProfile.age}
                          onChange={(e) => {
                            const val = e.target.value;
                            setNewProfile({...newProfile, age: val});
                            // Validate age range 4-10 inclusive
                            if (val === '') {
                              setAgeError('');
                            } else {
                              const n = parseInt(val, 10);
                              if (isNaN(n)) {
                                setAgeError('Please enter a valid age');
                              } else if (n < 4 || n > 10) {
                                setAgeError('Please enter the age from 4 to 10');
                              } else {
                                setAgeError('');
                              }
                            }
                          }}
                          placeholder="Age (4-10)"
                          className="rounded-xl border-2 border-[#EDE9FF] focus:border-[#8E7BFF] h-12 mt-2"
                        />
                        {ageError && (
                          <p className="text-red-600 text-sm mt-2 bg-red-50 border border-red-200 rounded-md px-3 py-1 inline-block">
                            {ageError}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label className="text-[#1F224B] font-semibold">Grade Level</Label>
                        <Select 
                          value={newProfile.gradeLevel !== undefined ? newProfile.gradeLevel.toString() : undefined}
                          onValueChange={(value) => setNewProfile({...newProfile, gradeLevel: parseInt(value)})}
                        >
                          <SelectTrigger className="rounded-xl border-2 border-[#EDE9FF] focus:border-[#8E7BFF] h-12 mt-2">
                            <SelectValue placeholder="Select grade level" />
                          </SelectTrigger>
                          <SelectContent>
                            {gradeLevels.map((grade, index) => (
                              <SelectItem key={index} value={index.toString()}>
                                {grade.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[#1F224B] font-semibold">Gender</Label>
                        <Select
                          value={newProfile.gender ?? undefined}
                          onValueChange={(value) => setNewProfile({...newProfile, gender: value as 'male' | 'female' | 'rather_not_say'})}
                        >
                          <SelectTrigger className="rounded-xl border-2 border-[#EDE9FF] focus:border-[#8E7BFF] h-12 mt-2">
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Boy</SelectItem>
                            <SelectItem value="female">Girl</SelectItem>
                            <SelectItem value="rather_not_say">Rather not say</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button 
                        onClick={handleAddProfile}
                        disabled={!newProfile.name.trim() || !newProfile.age || newProfile.gradeLevel === undefined || !newProfile.gender || !!ageError}
                        className={`w-full rounded-2xl h-12 font-bold shadow-lg transition-all ${
                          !newProfile.name.trim() || !newProfile.age || newProfile.gradeLevel === undefined || !newProfile.gender || !!ageError
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-95 text-white'
                        }`}
                      >
                        Create Profile ✨
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Profile Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {profiles.length === 0 ? (
                  <div className="col-span-full">
                    {/* Welcome Information for New Users */}
                    <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-3xl p-6 md:p-8 border-2 border-blue-200 mb-8">
                      <div className="text-center mb-6">
                        <h3 className="text-2xl md:text-3xl font-bold text-[#1F224B] mb-4">
                          Welcome to The Dear Parent Project
                        </h3>
                        <div className="w-24 h-1 bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] rounded-full mx-auto"></div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-6">
                        {/* Left Column - About Learning */}
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] flex items-center justify-center flex-shrink-0 mt-1">
                              <Heart className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <p className="text-[#1F224B] font-medium leading-relaxed">
                                Every child learns in their own special way.
                              </p>
                              <p className="text-[#6B6FA3] text-sm mt-1">
                                We're here to help you understand their learning journey and support them with confidence and joy.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Right Column - How It Works */}
                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#A6A1F7] to-[#8E7BFF] flex items-center justify-center flex-shrink-0 mt-1">
                              <BookOpen className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <p className="text-[#1F224B] font-medium leading-relaxed">
                                In just a few minutes, your child will take few Assesmnets designed like a fun listening game.
                              </p>
                              <p className="text-[#6B6FA3] text-sm mt-1">
                                You'll then see a simple report that shows how your child hears and builds words, plus tips for what to focus on next.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Call to Action */}
                      <div className="text-center border-t border-gray-200 pt-6">
                        <p className="text-[#6B6FA3] text-sm mb-4">
                          Ready to get started? Add your child's profile using the button above to begin their learning journey.
                        </p>
                        <div className="flex items-center justify-center gap-2 text-xs text-[#8E7BFF]">
                          <span>✨</span>
                          <span>Safe</span>
                          <span>•</span>
                          <span>Educational</span>
                          <span>•</span>
                          <span>Fun</span>
                          <span>✨</span>
                        </div>
                      </div>
                    </div>

                    {/* Empty State */}
                    <div className="text-center py-8">
                      <img src={ekoIcon} alt="Eko mascot" className="w-16 h-16 mb-4 animate-gentle-bounce object-contain inline-block" />
                      <h3 className="text-xl font-semibold text-gray-600 mb-2">No profiles yet!</h3>
                      <p className="text-gray-500">Click "Add Child" above to create your first profile</p>
                    </div>
                  </div>
                ) : (
                  profiles.map((profile) => {
                    // pick an icon based on name hash for visual variety
                    const seed = profile.name.charCodeAt(0) % 4;
                    const AvatarIcon = [Star, BookOpen, Trophy, Gift][seed] || Star;
                    const pastelBg = ["bg-[#FFF4E8]","bg-[#F5F7FF]","bg-[#FFF0F6]","bg-[#F0FFF4]"][(profile.name.charCodeAt(0) % 4)];

                    return (
                      <Card
                        key={profile.id}
                        className={`relative flex items-center px-4 py-3 rounded-2xl border-2 shadow-sm min-h-[64px] cursor-pointer transition-all duration-200 ${selectedProfile?.id === profile.id ? 'bg-gradient-to-r from-[#FFF7EE] to-[#FFF0F6] border-[#F7B26A] shadow-lg ring-2 ring-[#F7B26A]/30' : 'bg-white border-[#F0EEFF] hover:border-[#ECA7C1] hover:shadow-md'}`}
                        onClick={() => handleProfileClick(profile)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleProfileClick(profile); } }}
                        aria-pressed={selectedProfile?.id === profile.id}
                      >
                        {/* Left block: fixed avatar + text (reserve space on the right for the delete button) */}
                        <div className="flex items-center gap-4 min-w-0 w-full">
                          <div className={`w-12 h-12 flex-none rounded-full ${pastelBg} flex items-center justify-center ring-1 ring-white shadow-sm`}> 
                            {profile.avatar ? (
                              <img src={profile.avatar} alt="Avatar" className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                              <AvatarIcon className="text-[#55608A] w-6 h-6" />
                            )}
                          </div>

                          <div className="min-w-0 flex-1 pr-12">
                            <div className="text-lg font-semibold text-[#1F224B] leading-tight break-words">
                              {profile.name}
                            </div>
                            <div className="flex items-center flex-wrap gap-2 mt-0.5">
                              <span className="text-sm text-[#6B6FA3]">{profile.age} yrs</span>
                              <span className="text-sm text-[#6B6FA3]">·</span>
                              <span className="text-sm text-[#6B6FA3]">Grade {gradeLevels[profile.gradeLevel].name}</span>
                              {profile.paymentStatus === 'paid' ? (
                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                                  <BadgeCheck className="w-3 h-3" />
                                  Unlocked
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8E7BFF] bg-[#F5F3FF] border border-[#8E7BFF]/30 rounded-full px-2 py-0.5">
                                  <Lock className="w-3 h-3" />
                                  {bypassActive ? 'Locked · Admin access' : 'Locked'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Delete button: hidden for paid children; absolutely positioned so every card has same right offset */}
                        {profile.paymentStatus !== 'paid' && (
                          <button
                            aria-label="Delete profile"
                            onClick={(e) => { e.stopPropagation(); setDeleteDialogOpen(profile.id); }}
                            className="absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-md bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white flex items-center justify-center hover:brightness-95 transition border border-white shadow-sm"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </Card>
                    );
                  })
                )}
              </div>

              {/* Mobile: Show Start Journey button when profile is selected */}
              {selectedProfile && isMobile && (
                <div className="mt-6 lg:hidden">
                  <Button 
                    onClick={() => setShowStartTestModal(true)}
                    className="w-full h-14 rounded-2xl font-bold text-lg shadow-lg bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-105 text-white"
                  >
                     Continue with {selectedProfile.name}
                  </Button>
                </div>
              )}

              {/* Delete Confirmation Dialog */}
              <Dialog open={Boolean(deleteDialogOpen)} onOpenChange={(open) => { if (!open) setDeleteDialogOpen(null); }}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    {/* Show avatar + playful title */}
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-[#FFF4E8] flex items-center justify-center ring-1 ring-white shadow-sm">
                        {/* find the profile to delete and show its avatar or initial */}
                        {(() => {
                          const p = profiles.find(x => x.id === deleteDialogOpen);
                          if (p?.avatar) return <img src={p.avatar} alt="avatar" className="w-10 h-10 rounded-full object-cover" />;
                          const SeedIcon = [Star, BookOpen, Trophy, Gift][(p?.name?.charCodeAt(0) ?? 0) % 4] || Star;
                          return <SeedIcon className="text-[#FF8A7A] w-6 h-6" />;
                        })()}
                      </div>
                      <div>
                        <DialogTitle>
                          {(() => {
                            const p = profiles.find(x => x.id === deleteDialogOpen);
                            return p ? `Say goodbye to ${p.name}?` : 'Delete profile?';
                          })()}
                        </DialogTitle>
                        <p className="text-xs text-[#6B6FA3]">We will remove their progress from this device.</p>
                      </div>
                    </div>
                  </DialogHeader>
                  <div className="py-4">
                    {(() => {
                      const p = profiles.find(x => x.id === deleteDialogOpen);
                      if (!p) return <p className="text-sm text-gray-700">Are you sure you want to delete this profile? This action cannot be undone.</p>;
                      return (
                        <div>
                          <p className="text-sm text-gray-700">Are you sure you want to remove <span className="font-semibold text-[#1F224B]">{p.name}</span> from the app?</p>
                          <p className="mt-2 text-sm text-[#6B6FA3]">It's okay — you can add them again later. If you just need to pause, consider leaving their profile in place.</p>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button variant="secondary" onClick={() => setDeleteDialogOpen(null)} disabled={deletingProfile}>Cancel</Button>
                    <Button 
                      className="bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] text-white hover:brightness-95" 
                      onClick={async () => { 
                        if (deleteDialogOpen) { 
                          await handleDeleteProfile(deleteDialogOpen); 
                          setDeleteDialogOpen(null); 
                        } 
                      }}
                      disabled={deletingProfile}
                    >
                      {deletingProfile ? "Deleting..." : "Say goodbye 👋"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </Card>
          </div>

          {/* Right Column - Game Selection (hidden on mobile) */}
          <div className="hidden lg:block">
            <Card className="p-6 bg-white/95 backdrop-blur border-4  rounded-3xl shadow-xl">


              {selectedProfile ? (
                <div className="space-y-6">
                  {/* Selected Profile Display */}
                  <div className="text-center p-4 bg-gradient-to-r from-[#FFF7EE] to-[#FFF0F6] rounded-2xl border border-[#F7B26A]/30">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-r  to-[#ECA7C1] flex items-center justify-center text-2xl shadow-lg">
                      🧒
                    </div>
                    <h3 className="text-xl font-bold text-[#1F224B]">{selectedProfile.name}</h3>
                    <p className="text-sm text-[#6B6FA3]">{selectedProfile.age} years old</p>
                  </div>

                  {/* Grade Selection */}
                  <div>
                    <Label className="text-[#1F224B] font-semibold mb-3 block">Select Grade Level</Label>
                    <Select 
                      value={selectedGradeLevel >= 0 ? selectedGradeLevel.toString() : undefined} 
                      onValueChange={(value) => setSelectedGradeLevel(parseInt(value))}
                    >
                      <SelectTrigger className="rounded-xl border-2 border-[#EDE9FF] focus:border-[#8E7BFF] h-12">
                        <SelectValue placeholder="Choose grade level" />
                      </SelectTrigger>
                      <SelectContent>
                        {gradeLevels.map((grade, index) => (
                          <SelectItem key={index} value={index.toString()}>
                            {grade.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Start Assessment Button */}
                  <Button 
                    onClick={handleStartJourney}
                    disabled={selectedGradeLevel < 0}
                    className={`w-full h-14 rounded-2xl font-bold text-lg shadow-lg transition-all ${
                      selectedGradeLevel < 0
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-105 text-white hover:shadow-xl'
                    }`}
                  >
                    {canStartAssessment(selectedProfile) ? '📝 Start Assessment' : '🔒 Unlock Assessment'}
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <img src={ekoIcon} alt="Eko mascot" className="w-14 h-14 mb-4 animate-gentle-float object-contain inline-block" />
                  <h3 className="text-lg font-semibold text-[#1F224B] mb-2">Select a Child</h3>
                  <p className="text-[#6B6FA3] text-sm">Click on a child's profile to get started</p>
                </div>
              )}
              {/* Removed duplicate Add Child button from right column for better action hierarchy */}
            </Card>

            {/* Quick Stats */}
            {/* {selectedProfile && (
              <Card className="p-4 mt-4 bg-white/95 backdrop-blur border-4 border-green-200 rounded-3xl shadow-xl">
                <h3 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                  <Trophy className="w-5 h-5" />
                  Progress Stats
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-green-700">Total Words:</span>
                    <span className="font-bold text-green-800">{selectedProfile.wordsCompleted}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-green-700">Attempts:</span>
                    <span className="font-bold text-green-800">{selectedProfile.totalWordsAttempted}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-green-700">Accuracy:</span>
                    <span className="font-bold text-green-800">{Math.round(selectedProfile.averageAccuracy || 0)}%</span>
                  </div>
                </div>
              </Card>
            )} */}
          </div>
        </div>

        {/* Start Test Modal for Mobile */}
        <Dialog open={showStartTestModal} onOpenChange={setShowStartTestModal}>
          <DialogContent className="max-w-sm w-[90vw] rounded-3xl p-0 border-4 fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="relative">
              {/* Close button */}
              <button
                onClick={() => setShowStartTestModal(false)}
                className="absolute right-4 top-4 z-10 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>

              <div className="p-4 sm:p-6">
                <DialogHeader className="mb-4">
                  <DialogTitle className="text-xl font-bold text-[#1F224B] flex items-center gap-2">
                    Start Assessment
                  </DialogTitle>
                </DialogHeader>

                {selectedProfile && (
                  <div className="space-y-5">
                    {/* Selected Profile Display */}
                    <div className="text-center p-4 bg-gradient-to-r from-[#FFF7EE] to-[#FFF0F6] rounded-2xl border border-[#F7B26A]/30">
                      <div className="w-14 h-14 mx-auto mb-2 rounded-full bg-gradient-to-r  to-[#ECA7C1] flex items-center justify-center text-xl shadow-lg">
                        🧒
                      </div>
                      <h3 className="text-lg font-bold text-[#1F224B]">{selectedProfile.name}</h3>
                      <p className="text-sm text-[#6B6FA3]">{selectedProfile.age} years old</p>
                    </div>

                    {/* Grade Selection */}
                    <div>
                      <Label className="text-[#1F224B] font-semibold mb-2 block">Select Grade Level</Label>
                      <Select 
                        value={selectedGradeLevel >= 0 ? selectedGradeLevel.toString() : undefined} 
                        onValueChange={(value) => setSelectedGradeLevel(parseInt(value))}
                      >
                        <SelectTrigger className="rounded-xl border-2 border-[#EDE9FF] focus:border-[#8E7BFF] h-12">
                          <SelectValue placeholder="Choose grade level" />
                        </SelectTrigger>
                        <SelectContent>
                          {gradeLevels.map((grade, index) => (
                            <SelectItem key={index} value={index.toString()}>
                              {grade.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Start Assessment Button */}
                    <Button 
                      onClick={() => {
                        handleStartJourney();
                        setShowStartTestModal(false);
                      }}
                      disabled={selectedGradeLevel < 0}
                      className={`w-full h-12 rounded-2xl font-bold shadow-lg transition-all ${
                        selectedGradeLevel < 0
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-105 text-white'
                      }`}
                    >
                      {canStartAssessment(selectedProfile) ? '📝 Start Assessment' : '🔒 Unlock Assessment'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Assessment Guidance Modal - shown when starting an assessment */}
        {showAssessmentGuide && (
          <div
            onClick={() => setShowAssessmentGuide(false)}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.45)' }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] px-6 py-5 flex items-start justify-between gap-3 flex-shrink-0">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl font-extrabold text-white">Before you begin</h2>
                    <p className="text-white/90 text-sm mt-0.5">
                      A few small things that help your child do their best — and give you the clearest picture.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAssessmentGuide(false)}
                  className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white flex-shrink-0 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                {/* Set the scene */}
                <div className="bg-[#FFF9F3] rounded-2xl p-4 border border-[#F7B26A]/15">
                  <h3 className="text-[#1F224B] font-bold mb-2 flex items-center gap-2 text-sm">
                    <span className="text-base">📍</span> Set the scene
                  </h3>
                  <ul className="list-disc pl-5 space-y-1 text-[13px] text-[#4B4B6B] leading-relaxed">
                    <li>Find a quiet spot where you won't be interrupted for about 20–30 minutes.</li>
                    <li>Pick a time when your child is calm and rested — mornings or after a snack often work best, not right before bed.</li>
                    <li>Have headphones ready if you can — some activities use sound.</li>
                    <li>Check your device is charged and the volume is on.</li>
                    <li>Sit at a table or steady surface — it makes tapping and typing easier.</li>
                  </ul>
                </div>

                {/* Settle your child */}
                <div className="bg-[#F9F8FF] rounded-2xl p-4 border border-[#8E7BFF]/15">
                  <h3 className="text-[#1F224B] font-bold mb-2 flex items-center gap-2 text-sm">
                    <span className="text-base">🧒</span> Settle your child
                  </h3>
                  <ul className="list-disc pl-5 space-y-1 text-[13px] text-[#4B4B6B] leading-relaxed">
                    <li>Tell them it's a set of fun activities, not a test — nothing to get wrong.</li>
                    <li>Reassure them it's okay to take their time, and okay to guess.</li>
                    <li>Let them do it themselves, the activities work best as their own responses.</li>
                    <li>Sit nearby for comfort if you like, just try not to point at answers.</li>
                  </ul>
                </div>

                {/* During the activities */}
                <div className="bg-[#FFF6FA] rounded-2xl p-4 border border-[#ECA7C1]/20">
                  <h3 className="text-[#1F224B] font-bold mb-2 flex items-center gap-2 text-sm">
                    <span className="text-base">🎯</span> During the activities
                  </h3>
                  <ul className="list-disc pl-5 space-y-1 text-[13px] text-[#4B4B6B] leading-relaxed">
                    <li>If your child gets tired, it's fine to pause and come back — progress is saved.</li>
                    <li>Let them use the audio and replay buttons as much as they like.</li>
                    <li>Keep it light — a little encouragement goes a long way.</li>
                  </ul>
                </div>

                {/* Gentle reminder */}
                <div className="bg-gradient-to-r from-[#FFF7EE] to-[#FFF0F6] border border-[#F7B26A]/30 rounded-2xl p-4 flex items-start gap-3">
                  <Heart className="w-5 h-5 text-[#FF8A7A] flex-shrink-0 mt-0.5" />
                  <p className="text-[13px] text-[#6B6FA3] leading-relaxed">
                    <span className="font-semibold text-[#1F224B]">A gentle reminder: </span>
                    This is a starting point for understanding how your child learns — not a test they can pass or fail. Every child is different, and that's exactly the point.
                  </p>
                </div>

                {/* Don't show again */}
                <label className="flex items-center gap-2 cursor-pointer text-sm text-[#6B6FA3] select-none">
                  <input
                    type="checkbox"
                    checked={dontShowGuide}
                    onChange={(e) => {
                      setDontShowGuide(e.target.checked);
                      localStorage.setItem('skip-assessment-guide', String(e.target.checked));
                    }}
                    className="w-4 h-4 accent-[#8E7BFF]"
                  />
                  Don't show this again
                </label>

                {/* Begin button */}
                <Button
                  onClick={handleBeginAssessment}
                  className="w-full h-13 py-4 rounded-2xl font-bold text-lg shadow-lg bg-gradient-to-r from-[#F7B26A] to-[#ECA7C1] hover:brightness-105 text-white"
                >
                  We're ready — let's begin →
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>


      
      <footer style={{ marginTop: 10, color: '#6B6FA3', fontSize: 12, textAlign: 'center' }}>© The Dear Parent Project</footer>
    </div>
  );
};
